/**
 * Per-car API client functions (M8.B price-breakdown endpoint).
 *
 * Mirrors `app/schemas/regression.py` (PriceBreakdownResponse / Row).
 */

export type BreakdownRowKind =
  | "baseline"
  | "adjustment"
  | "expected"
  | "listed"
  | "residual"
  | "null_effects_summary"
  // VED luxury-tax (ECS) saving — a buyer-side adjustment shown AFTER the
  // market expected/listed/residual rows (#59), not folded into them.
  | "luxury_tax"
  // Listed vs (expected + the luxury-tax saving) — the buyer view, trailing
  // `residual`. Never affects the classification band (#59).
  | "buyer_adjusted";

export type ResidualClassification =
  | "well_below"
  | "below"
  | "at_market"
  | "above"
  | "well_above";

export interface PriceBreakdownRow {
  kind: BreakdownRowKind;
  label: string;
  sublabel?: string | null;
  value_pence?: number | null;
  delta_pence?: number | null;
  uncertainty_pence?: number | null;
  classification?: ResidualClassification | null;
  is_null_effect?: boolean | null;
  group?: string | null;
  /** gck303/evtracker#60 — set on the residual row when the car's snapshot
   *  for this regression carries an error-scaled z (loo_residual / se_obs);
   *  null when classification fell back to the percentage bands. */
  z?: number | null;
  /** #60 — leverage-guard flag: true means "no comparable peers", the
   *  classification above is not a meaningful value signal for this car. */
  no_peers?: boolean | null;
}

export interface PriceBreakdownResponse {
  car_id: string;
  model_code: string | null;
  regression_fitted_at: string | null;
  regression_n_observations: number | null;
  regression_r_squared: number | null;
  rows: PriceBreakdownRow[];
}

export async function fetchPriceBreakdown(carId: string): Promise<PriceBreakdownResponse> {
  const res = await fetch(`/api/cars/${carId}/price-breakdown`);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      message?: string;
      detail?: string;
    };
    throw new Error(body.detail ?? body.message ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<PriceBreakdownResponse>;
}
